// Versão: 2.00
// Data..: 2026-09-24
// Portal público: hero com busca + filtro por categoria + paleta de busca (Ctrl+K).

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PublicShell } from './PublicShell.js';
import { publicApi, type PublicArticle, type PublicCategory } from '../../lib/publicApi.js';

export function PublicHomePage() {
  const [articles, setArticles] = useState<PublicArticle[]>([]);
  const [categories, setCategories] = useState<PublicCategory[]>([]);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      publicApi.listArticles().then((r) => r.items).catch(() => [] as PublicArticle[]),
      publicApi.listCategories().then((r) => r.items).catch(() => [] as PublicCategory[]),
    ]).then(([arts, cats]) => { setArticles(arts); setCategories(cats); }).finally(() => setLoading(false));
  }, []);

  // Atalho Ctrl+K / Cmd+K abre a paleta de busca.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if (e.key === 'Escape') setPaletteOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((a) => {
      const matchCat = activeCategory === 'all' || a.categoryId === activeCategory;
      const matchQ = !q || `${a.title} ${(a.tags ?? []).join(' ')}`.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [articles, query, activeCategory]);

  return (
    <PublicShell>
      {/* Hero */}
      <section
        style={{
          background:
            'radial-gradient(800px 360px at 50% -10%, rgba(255,255,255,0.2), transparent 60%), linear-gradient(135deg, #0088cc 0%, #6c3ce0 100%)',
          color: '#fff',
          padding: '80px 24px 88px',
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 720, margin: '0 auto' }} className="fade-in">
          <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 40, color: '#fff', marginBottom: 10, letterSpacing: '-0.02em' }}>
            Como podemos ajudar?
          </h1>
          <p style={{ fontSize: 17, opacity: 0.85, marginBottom: 26 }}>
            Busque na base de conhecimento ou abra um chamado com nosso time de suporte.
          </p>
          <div
            className="flex items-center"
            style={{
              background: '#fff', borderRadius: 'var(--radius)', padding: '6px 6px 6px 16px',
              boxShadow: 'var(--shadow-lg)', maxWidth: 560, margin: '0 auto',
            }}
          >
            <span style={{ fontSize: 18 }}>🔎</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar artigos de ajuda... (ou tecle Ctrl K)"
              style={{
                flex: 1, border: 'none', outline: 'none', padding: '10px 12px',
                fontSize: 15, color: '#111', background: 'transparent',
              }}
            />
            <Link to="/portal/abrir" className="btn btn-primary">Abrir chamado</Link>
          </div>
        </div>
      </section>

      {/* Artigos */}
      <section style={{ maxWidth: 960, margin: '-32px auto 48px', padding: '0 24px' }}>
        <div className="card fade-in">
          <div className="flex items-center justify-between mb-4" style={{ flexWrap: 'wrap', gap: 'var(--s2)' }}>
            <h2 className="mb-0">Base de conhecimento</h2>
            <span className="text-dim text-sm">{filtered.length} artigo(s)</span>
          </div>

          {/* Filtro por categoria (chips) */}
          {categories.length > 0 && (
            <div className="flex gap-2 mb-4" style={{ flexWrap: 'wrap' }}>
              <CategoryChip label="Todos" active={activeCategory === 'all'} onClick={() => setActiveCategory('all')} />
              {categories.map((c) => (
                <CategoryChip key={c.id} label={c.name} active={activeCategory === c.id} onClick={() => setActiveCategory(c.id)} />
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center" style={{ padding: 32 }}><span className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">📚</div>
              {query || activeCategory !== 'all' ? 'Nenhum artigo encontrado.' : 'Ainda não há artigos publicados.'}
            </div>
          ) : (
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--s4)' }}>
              {filtered.map((a) => (
                <Link
                  key={a.id}
                  to={`/portal/kb/${a.slug}`}
                  className="card card-hover"
                  style={{ padding: 'var(--s4)', display: 'block', color: 'var(--text)' }}
                >
                  <div style={{ fontSize: 22, marginBottom: 8 }}>📄</div>
                  <div className="font-semibold">{a.title}</div>
                  <div className="text-dim text-sm mt-2">
                    Atualizado em {new Date(a.updatedAt).toLocaleDateString('pt-BR')}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {paletteOpen && <SearchPalette articles={articles} onClose={() => setPaletteOpen(false)} />}
    </PublicShell>
  );
}

function CategoryChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="btn btn-sm"
      style={{
        borderRadius: 999,
        background: active ? 'var(--brand)' : 'var(--surface-2)',
        color: active ? '#fff' : 'var(--text-2)',
        border: '1px solid var(--border)',
      }}
    >
      {label}
    </button>
  );
}

// Paleta de busca (Ctrl+K): filtra artigos por título/tag e navega ao selecionado.
function SearchPalette({ articles, onClose }: { articles: PublicArticle[]; onClose: () => void }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');

  const results = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return articles.slice(0, 8);
    return articles
      .filter((a) => `${a.title} ${(a.tags ?? []).join(' ')}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [articles, term]);

  return (
    <div className="modal-backdrop" style={{ alignItems: 'flex-start', paddingTop: '12vh' }} onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560, padding: 0 }} onClick={(e) => e.stopPropagation()}>
        <input
          autoFocus
          className="input"
          placeholder="Buscar artigos..."
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          style={{ border: 'none', borderBottom: '1px solid var(--border)', borderRadius: 0, fontSize: 16, padding: 16 }}
        />
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {results.length === 0 ? (
            <div className="empty" style={{ padding: 28 }}><div className="empty-icon">🔍</div>Nenhum artigo encontrado.</div>
          ) : (
            results.map((a) => (
              <button
                key={a.id}
                onClick={() => { onClose(); navigate(`/portal/kb/${a.slug}`); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px',
                  border: 'none', borderBottom: '1px solid var(--border)', background: 'transparent', cursor: 'pointer',
                }}
              >
                <span style={{ marginRight: 8 }}>📄</span>
                <span className="font-semibold" style={{ color: 'var(--text)' }}>{a.title}</span>
              </button>
            ))
          )}
        </div>
        <div className="text-dim text-xs" style={{ padding: '8px 16px', borderTop: '1px solid var(--border)' }}>
          Enter para abrir · Esc para fechar
        </div>
      </div>
    </div>
  );
}
